import * as THREE from 'three';

// Bind authored named specials to their existing draw instances. Matrices use
// native scene coordinates; the renderer's parent owns the handedness change.
export function createSceneBindings(parsed, meshes) {
  const objects = new Map();
  for (let index = 0; index < parsed.specialObjects.length; index++) {
    const special = parsed.specialObjects[index], key = special.name.toLowerCase();
    const entry = { index, name: special.name, originalMatrix: special.matrix ?? special.parts[0]?.matrix,
      visible: special.visible, meshes: meshes.filter(mesh => mesh.userData.specialIndex === index) };
    entry.matrix = entry.originalMatrix ? new THREE.Matrix4().fromArray(entry.originalMatrix) : null;
    if (!objects.has(key)) objects.set(key, []);
    objects.get(key).push(entry);
  }
  const resolve = name => objects.get(String(name).toLowerCase()) ?? [];
  return {
    objects,
    hasObject(name) { return resolve(name).length > 0; },
    getObject(name) { return resolve(name)[0] ?? null; },
    setVisible(name, visible) {
      if (typeof visible !== 'boolean') throw new Error('Object visibility must be boolean.');
      const entries = resolve(name);
      for (const entry of entries) {
        entry.visible = visible;
        for (const mesh of entry.meshes) { mesh.userData.runtimeVisible = visible; mesh.visible = visible; }
      }
      return entries.length > 0;
    },
    setMatrix(name, values) {
      if ((!Array.isArray(values) && !ArrayBuffer.isView(values)) || values.length !== 16 || ![...values].every(Number.isFinite)) throw new Error('Invalid scene object matrix.');
      const entries = resolve(name);
      for (const entry of entries) {
        if (!entry.matrix) entry.matrix = new THREE.Matrix4();
        entry.matrix.fromArray(values);
        for (const mesh of entry.meshes) { mesh.matrix.fromArray(values); mesh.matrixWorldNeedsUpdate = true; }
      }
      return entries.length > 0;
    },
    restore(name) {
      const entries = resolve(name);
      for (const entry of entries) {
        entry.visible = parsed.specialObjects[entry.index].visible;
        if (entry.originalMatrix) entry.matrix.fromArray(entry.originalMatrix);
        for (const mesh of entry.meshes) {
          if (entry.originalMatrix) mesh.matrix.fromArray(entry.originalMatrix);
          mesh.matrixWorldNeedsUpdate = true;
          delete mesh.userData.runtimeVisible; mesh.visible = entry.visible;
        }
      }
      return entries.length > 0;
    },
    position(name) {
      const matrix = resolve(name)[0]?.matrix;
      return matrix ? new THREE.Vector3().setFromMatrixPosition(matrix).toArray() : null;
    },
  };
}
